import React, {FC, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef} from 'react';

import {KeyboardActionsEnum} from '../../../../core/src/helpers/browser/keyboardHelpers';
import {InteractionComponentProps} from '../../../../core/src/helpers/interaction/interactionHelpers';
import {useMaybeControlledState} from '../../../../core/src/helpers/react/hooks/useMaybeControlledState';
import {
  ClickEventHandler,
  FrontKeyboardEvent,
  RendererOf,
} from '../../../../core/src/helpers/react/reactHelpers';
import {MethodWrapper, WrappedMethod} from '../../../../core/src/helpers/typescript/typeHelpers';
import {ChromelessButton} from '../buttons/chromelessButton';
import {KeyboardActionEvent} from '../keyboard/keyboardContext';
import {KeyboardShortcuts} from '../keyboard/keyboardShortcuts';

/*
 * Props.
 */

interface PopoverTransferProps {
  anchor: RefObject<HTMLElement>;
  anchorWidth?: number;
  onRequestClose: () => void;
  wrap: MethodWrapper;
}

interface ControlledPopoverCoordinatorProps extends InteractionComponentProps {
  renderButton: (isModalOpen: boolean) => ReactNode;
  renderModal: RendererOf<PopoverTransferProps>;
  keyboardAction?: KeyboardActionsEnum;
  onPopoverToggle?: (isOpen: boolean, keyboardAction?: KeyboardActionsEnum) => void;
  className?: string;
  shouldPreventMouseDown?: boolean;
  shouldHoverWhenOpen?: boolean;
  children?: RendererOf<RefObject<HTMLElement>>;
  isDisabled?: boolean;
  isModalInitiallyOpen?: boolean;
  targetRef?: RefObject<HTMLDivElement>;
  tabIndex?: number;
  notifyOnPopoverToggle?: () => void;
  isPopoverOpen?: boolean;
  onSetIsPopoverOpen?: (latestIsPopoverOpen: boolean) => void;
}

/*
 * Component.
 */

// PopoverCoordinator is being migrated, so it utilizes ControlledPopoverCoordinator.
// Please use PopoverCoordinator instead.
export const ControlledPopoverCoordinator: FC<ControlledPopoverCoordinatorProps> = (props) => {
  const {
    renderButton,
    renderModal,
    keyboardAction,
    onPopoverToggle,
    className,
    shouldPreventMouseDown,
    shouldHoverWhenOpen,
    children,
    isDisabled,
    isModalInitiallyOpen,
    targetRef,
    tabIndex,
    notifyOnPopoverToggle,
    interactionId,
    isPopoverOpen,
    onSetIsPopoverOpen,
  } = props;

  const buttonRef = useRef<HTMLDivElement>(null);

  const [isModalOpen, setIsModalOpen] = useMaybeControlledState({
    defaultValue: false,
    controlledValue: isPopoverOpen,
    onChange: onSetIsPopoverOpen,
  });

  const keyboardActionRef = useRef<KeyboardActionsEnum | undefined>(undefined);

  useEffect(() => {
    // The modal need a ref to the button to be opened. This ref does not exist at the first render.
    // To have the modal opened on mount, we have to render again and we will be able to use the ref.
    if (isModalInitiallyOpen) {
      setIsModalOpen(true);
    }
  }, [setIsModalOpen, isModalInitiallyOpen]);

  const toggleIsModalOpen = useCallback(
    (keyboardActionValue?: KeyboardActionsEnum) => {
      keyboardActionRef.current = keyboardActionValue;
      setIsModalOpen((prevIsModalOpen) => !prevIsModalOpen);
    },
    [setIsModalOpen],
  );

  const onRequestClose = useCallback(() => {
    keyboardActionRef.current = undefined;
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const onPopoverToggleRef = useRef(onPopoverToggle);

  useEffect(() => {
    onPopoverToggleRef.current = onPopoverToggle;
  }, [onPopoverToggle]);

  useEffect(() => {
    if (!onPopoverToggleRef.current) {
      return;
    }

    onPopoverToggleRef.current(isModalOpen, keyboardActionRef.current);
  }, [isModalOpen]);

  const wrap = useCallback(
    <T extends ((...args: Array<any>) => any) | undefined>(method: T) => {
      // Create a wrapper that closes the modal before calling the method.
      const wrapper = (...args: Array<any>) => {
        onRequestClose();
        return method && method(...args);
      };

      return wrapper as WrappedMethod<T>;
    },
    [onRequestClose],
  );

  const onButtonClick: ClickEventHandler = useCallback(
    (event) => {
      if (isDisabled) {
        return;
      }

      notifyOnPopoverToggle?.();
      event.preventDefault();
      event.stopPropagation();
      toggleIsModalOpen();
    },
    [isDisabled, notifyOnPopoverToggle, toggleIsModalOpen],
  );

  const renderModalContent = useCallback(() => {
    const currentButton = buttonRef.current;
    if (!currentButton) {
      return null;
    }

    const modalProps: PopoverTransferProps = {
      anchor: targetRef?.current ? targetRef : buttonRef,
      anchorWidth: currentButton.offsetWidth,
      onRequestClose,
      wrap,
    };
    return renderModal(modalProps);
  }, [onRequestClose, wrap, renderModal]);

  const renderContent = useCallback(() => {
    return (
      <>
        <ChromelessButton
          ref={buttonRef}
          onClick={onButtonClick}
          className={className}
          hasHover={shouldHoverWhenOpen && isModalOpen}
          shouldPreventMouseDown={shouldPreventMouseDown}
          isOpen={isModalOpen}
          isDisabled={isDisabled}
          interactionId={interactionId}
          tabIndex={tabIndex}
        >
          {renderButton(isModalOpen)}
        </ChromelessButton>
        {isModalOpen && renderModalContent()}
        {children && children(buttonRef)}
      </>
    );
  }, [
    onButtonClick,
    className,
    shouldHoverWhenOpen,
    isModalOpen,
    shouldPreventMouseDown,
    isDisabled,
    interactionId,
    tabIndex,
    renderButton,
    renderModalContent,
    children,
  ]);

  // If no keyboard action was provided, render directly.
  if (!keyboardAction) {
    return renderContent();
  }

  return (
    <KeyboardPopoverCoordinator
      keyboardAction={keyboardAction}
      isDisabled={isDisabled ?? false}
      toggleIsModalOpen={toggleIsModalOpen}
      notifyOnPopoverToggle={notifyOnPopoverToggle}
    >
      {renderContent()}
    </KeyboardPopoverCoordinator>
  );
};

const KeyboardPopoverCoordinator: FC<{
  children: ReactNode;
  keyboardAction: KeyboardActionsEnum;
  isDisabled: boolean;
  toggleIsModalOpen: (keyboardActionValue?: KeyboardActionsEnum | undefined) => void;
  notifyOnPopoverToggle: (() => void) | undefined;
}> = ({children, keyboardAction, isDisabled, toggleIsModalOpen, notifyOnPopoverToggle}) => {
  const onKeyboardAction = useCallback(
    (event: FrontKeyboardEvent, action: KeyboardActionEvent) => {
      if (isDisabled) {
        return;
      }

      notifyOnPopoverToggle?.();
      toggleIsModalOpen(action.action);
    },
    [isDisabled, notifyOnPopoverToggle, toggleIsModalOpen],
  );

  // Listen for key presses.
  const keyHandlers = useMemo(
    () => ({
      [keyboardAction]: onKeyboardAction,
    }),
    [keyboardAction, onKeyboardAction],
  );

  return <KeyboardShortcuts handlers={keyHandlers}>{children}</KeyboardShortcuts>;
};
